/* eslint-disable react/no-unescaped-entities */
import { Container, Divider, Heading, NextLink, Paper, Stack } from "@bilar/ui";
import Head from "next/head";
import {
	BasePageProps,
	useAppTranslation,
	useContentStyles,
} from "@bilar/common";
import {
	GetStaticProps,
	GetStaticPropsContext,
	GetStaticPropsResult,
} from "next";
import { getBasePagePropsData } from "@bilar/pages-common";
import { getConfig, routes } from "@bilar/config";
import React from "react";

type PrivacyPageProps = {} & BasePageProps;

const HowToPage = (props: PrivacyPageProps) => {
	const { list, orderedList, unOrderedList } = useContentStyles();
	const { t } = useAppTranslation();
	const { baseUrl } = getConfig().public;
	return (
		<>
			<Head>
				<title>Bílar.is - How it works</title>
				<link
					rel="canonical"
					href={`${baseUrl}/en/how-it-works/en`}
					key="canonical"
				/>
				<link
					rel="alternate"
					hrefLang="x-default"
					href={`${baseUrl}/how-it-works`}
					key="alternate-x-default"
				/>
				<link
					rel="alternate"
					hrefLang="is"
					href={`${baseUrl}/how-it-works`}
					key="alternate-is"
				/>
				<link
					rel="alternate"
					hrefLang="en"
					href={`${baseUrl}/en/how-it-works/en`}
					key="alternate-en"
				/>
			</Head>{" "}
			<Container>
				<Heading as="h1" size="large">
					How it works
				</Heading>
				<p>
					Welcome to Bílar.is, your go-to marketplace for{" "}
					<NextLink href={routes.vehicleClassifieds.new}>
						<strong>creating</strong>
					</NextLink>{" "}
					and browsing{" "}
					<strong>
						<NextLink href={routes.vehicleClassifieds.index}>
							classified ads
						</NextLink>
					</strong>{" "}
					for vehicles.
				</p>
				<p>
					Whether you're looking to sell your vehicle or in the market for a new
					one, our platform simplifies the process of connecting sellers with
					potential buyers.{" "}
				</p>
				<p>Here's how you can get started:</p>

				<Divider my={6} variant="dotted" />

				<Stack orientation={["vertical", "horizontal"]} spacing={12} mb={6}>
					<Paper as="section" width={["100%", "50%"]} p={4}>
						<Heading as="h2">For Sellers</Heading>

						<ol css={[list.root, orderedList.root]}>
							<li css={[list.item, orderedList.item]}>
								<Heading as="h3" size="small">
									Create your listing
								</Heading>
								<ul css={[list.root, unOrderedList.root]}>
									<li css={[list.item, unOrderedList.item]}>
										<p>
											<strong>Sign Up & Post</strong>: Select the option to{" "}
											<NextLink href={routes.vehicleClassifieds.new}>
												{t("postNewAd")}
											</NextLink>
											, sign up and provide all the necessary details about your
											vehicle, such as make, model, year, condition, mileage,
											and asking price.
										</p>
										<p>
											Remember, including high-quality photos can significantly
											increase the visibility and appeal of your ad.
										</p>
										<p>
											In addition, our built-in image editor lets you improve
											your photos, ideal for hiding sensitive details such as
											your vehicle's <strong>number plate</strong>, combining
											privacy with ad integrity.
										</p>
									</li>
								</ul>
							</li>
							<li css={[list.item, orderedList.item]}>
								<Heading as="h3" size="small">
									Wait for Contact
								</Heading>
								<ul css={[list.root, unOrderedList.root]}>
									<li css={[list.item, unOrderedList.item]}>
										<strong>Be Contactable</strong>: Make sure to include
										accurate contact information in your ad, such as a phone
										number or email address, so interested buyers can reach out
										to you directly.
									</li>
									<li css={[list.item, unOrderedList.item]}>
										<strong>Manage Your Ad</strong>: In your{" "}
										<NextLink href={routes.profile.classifieds}>
											{t("myClassifieds")}
										</NextLink>{" "}
										profile section, you can manage your listings, update
										details.
										{/*, and check ad performance*/}
										{/*at any time.*/}
									</li>
								</ul>
							</li>
							<li css={[list.item, orderedList.item]}>
								<Heading as="h3" size="small">
									Finalize the Sale
								</Heading>
								<ul css={[list.root, unOrderedList.root]}>
									<li css={[list.item, unOrderedList.item]}>
										<strong>Direct Communication</strong>: All negotiations,
										viewings, and sales are conducted directly between you and
										the buyer.
									</li>
									<li css={[list.item, unOrderedList.item]}>
										<strong>Safe Transaction Tips</strong>: We recommend meeting
										in public places for viewings and transactions, checking the
										buyer's or seller's identity, and following best practices
										for safe payments.
										<br />
										<br />
										<p>
											See more on Samgöngustofa's page for{" "}
											<NextLink
												href="https://island.is/en/car-purchases"
												target="_blank"
											>
												Purchases and sales of vehicles
											</NextLink>
										</p>
									</li>
								</ul>
							</li>
						</ol>
					</Paper>
					<Paper as="section" width={["100%", "50%"]} p={4}>
						<Heading as="h2">For Buyers</Heading>
						<ol css={[list.root, orderedList.root]}>
							<li css={[list.item, orderedList.item]}>
								<Heading as="h3" size="small">
									Find the Right Vehicle
								</Heading>
								<ul css={[list.root, unOrderedList.root]}>
									<li css={[list.item, unOrderedList.item]}>
										<strong>Browse Posts:</strong>{" "}
										<NextLink href={routes.vehicleClassifieds.index}>
											{t("viewAllVehicles")}
										</NextLink>{" "}
										or use the search and filter tools on Bílar.is to find the
										vehicle that meets your needs. You can sort listings by
										make, model, price, age and more.
									</li>
									<li css={[list.item, unOrderedList.item]}>
										<strong>Vehicle Information:</strong> Click on any listing
										for detailed information and to view photos of the vehicle.
									</li>
								</ul>
							</li>
							<li css={[list.item, orderedList.item]}>
								<Heading as="h3" size="small">
									Contact the Seller
								</Heading>
								<ul css={[list.root, unOrderedList.root]}>
									<li css={[list.item, unOrderedList.item]}>
										<strong>Reach Out:</strong> When you find a vehicle you're
										interested in, contact the seller directly using the
										information provided in the post, typically through a phone
										call or email.
									</li>
									<li css={[list.item, unOrderedList.item]}>
										<strong>Ask Questions:</strong> Don't hesitate to ask the
										seller for more details about the vehicle, additional
										photos, or to arrange a viewing or test drive.
									</li>
								</ul>
							</li>
						</ol>
					</Paper>
				</Stack>
			</Container>
		</>
	);
};
export const getStaticProps: GetStaticProps<PrivacyPageProps> = async (
	ctx: GetStaticPropsContext,
): Promise<GetStaticPropsResult<PrivacyPageProps>> => {
	// Get Locale and common translations
	const baseProps = await getBasePagePropsData(ctx);

	return {
		props: {
			...baseProps,
		},
		// we will attempt to re-generate the page:
		// - when a request comes in
		// - at most once every second
		//revalidate: 60,
	};
};
export default HowToPage;
